import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import {
  orgsApi,
  type AppKey,
  type BootstrapInviteResult,
  type OrgRow,
  type OrgUserRow,
  type OrgUserPatch,
  type ResyncSummary,
} from '../features/orgs/api.ts';
import { subscriptionsApi } from '../features/subscriptions/api.ts';
import { AppRoleChips, AppRoleEditor } from '../features/appRoles/AppRoles.tsx';
import {
  AppAccessFields,
  emptyAppAccess,
  appAccessError,
  toAppProvisioning,
  type AppAccessState,
} from '../features/invites/AppAccessFields.tsx';

function RoleBadge({ user }: { user: OrgUserRow }): JSX.Element {
  const label = user.role === 'SYS_ADMIN' ? 'System admin' : user.isOrgAdmin ? 'Admin' : 'Member';
  const cls = user.role === 'SYS_ADMIN' || user.isOrgAdmin ? 'pill-active' : 'pill-neutral';
  return <span className={`pill ${cls}`}>{label}</span>;
}

// Org display-name header with an inline edit affordance scoped to `name` +
// `nameDv` ONLY (contactEmail/status have their own controls). Saving triggers
// the backend fan-out to linked apps.
function OrgNameHeader({
  org,
  onSave,
  busy,
  error,
  savedAt,
}: {
  org: OrgRow;
  onSave: (patch: { name: string; nameDv: string }) => void;
  busy: boolean;
  error: boolean;
  savedAt: number;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(org.name);
  const [nameDv, setNameDv] = useState(org.nameDv ?? '');

  // Re-seed the fields whenever the org (re)loads.
  useEffect(() => {
    setName(org.name);
    setNameDv(org.nameDv ?? '');
  }, [org.name, org.nameDv]);

  // Collapse the editor once a save succeeds.
  useEffect(() => {
    if (savedAt) setOpen(false);
  }, [savedAt]);

  if (!open) {
    return (
      <header>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[2rem] md:text-[2.5rem] font-normal leading-tight text-ink">
            {org.name}
          </h1>
          {org.nameDv && (
            <span dir="rtl" className="font-dhivehi text-2xl text-foreground">
              {org.nameDv}
            </span>
          )}
          <button type="button" onClick={() => setOpen(true)} className="btn-secondary btn-sm">
            <Icon name="edit" size={16} /> Edit name
          </button>
        </div>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{org.contactEmail}</span>
          <span className={`pill ${org.status === 'ACTIVE' ? 'pill-success' : 'pill-danger'}`}>
            {org.status}
          </span>
          <span>· {org._count.users} user(s)</span>
        </p>
      </header>
    );
  }
  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Org name"
          aria-label="Org name"
          className="input w-56 py-2 text-base"
        />
        <input
          dir="rtl"
          value={nameDv}
          onChange={(e) => setNameDv(e.target.value)}
          placeholder="ކައުންސިލްގެ ދިވެހި ނަން"
          aria-label="Org name (Dhivehi)"
          className="input w-56 py-2 text-base font-dhivehi text-right"
        />
        <button
          type="button"
          disabled={busy || !name.trim() || !nameDv.trim()}
          onClick={() => onSave({ name: name.trim(), nameDv: nameDv.trim() })}
          className="btn-secondary btn-sm"
        >
          <Icon name="check" size={16} /> Save
        </button>
        <button
          type="button"
          aria-label="Cancel editing the name"
          onClick={() => {
            setName(org.name);
            setNameDv(org.nameDv ?? '');
            setOpen(false);
          }}
          className="btn-ghost btn-sm"
        >
          <Icon name="close" size={16} />
        </button>
      </div>
      {error && <p className="text-sm text-destructive">Couldn&rsquo;t save the name — try again.</p>}
    </header>
  );
}

function UserEditor({
  user,
  onSave,
  busy,
}: {
  user: OrgUserRow;
  onSave: (patch: OrgUserPatch) => void;
  busy: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [nameDv, setNameDv] = useState(user.nameDv ?? '');
  const [dv, setDv] = useState(user.designationDv ?? '');
  const [en, setEn] = useState(user.designationEn ?? '');

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="btn-secondary btn-sm">
        <Icon name="edit" size={16} /> Name &amp; designation
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        dir="rtl"
        value={nameDv}
        onChange={(e) => setNameDv(e.target.value)}
        placeholder="ނަން"
        aria-label="Name (Dhivehi)"
        className="input w-36 py-2 text-base font-dhivehi text-right"
      />
      <input
        dir="rtl"
        value={dv}
        onChange={(e) => setDv(e.target.value)}
        placeholder="މަޤާމު"
        aria-label="Designation (Dhivehi)"
        className="input w-36 py-2 text-base font-dhivehi text-right"
      />
      <input
        value={en}
        onChange={(e) => setEn(e.target.value)}
        placeholder="Designation (English)"
        aria-label="Designation (English)"
        className="input w-36 py-2 text-xs"
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          onSave({ nameDv: nameDv || null, designationDv: dv || null, designationEn: en || null });
          setOpen(false);
        }}
        className="btn-secondary btn-sm"
      >
        <Icon name="check" size={16} /> Save
      </button>
      <button
        type="button"
        aria-label="Cancel editing"
        onClick={() => setOpen(false)}
        className="btn-ghost btn-sm"
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}

const APP_ROWS = [
  { key: 'wapmeet', name: 'WapMeet' },
  { key: 'wapstat', name: 'WapStat' },
  { key: 'wapsign', name: 'WapSign' },
  { key: 'wappro', name: 'WapPro' },
  { key: 'wapdocs', name: 'WapDocs' },
] as const;

export function AdminOrgDetail(): JSX.Element {
  const { id } = useParams({ from: '/admin/orgs/$id' });
  const qc = useQueryClient();
  const { data: org } = useQuery({ queryKey: ['admin', 'orgs', 'detail', id], queryFn: () => orgsApi.get(id) });
  const { data: users } = useQuery({ queryKey: ['admin', 'orgs', 'users', id], queryFn: () => orgsApi.listUsers(id) });
  const { data: appRoles } = useQuery({ queryKey: ['admin', 'orgs', 'app-roles', id], queryFn: () => orgsApi.appRoles(id) });

  const [email, setEmail] = useState('');
  const [appAccess, setAppAccess] = useState<AppAccessState>(emptyAppAccess);
  const [result, setResult] = useState<BootstrapInviteResult | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState<Record<string, boolean>>({});
  const [editAppRoles, setEditAppRoles] = useState<Record<string, boolean>>({});

  // Subscription panel
  const [paidUntil, setPaidUntil] = useState('');
  useEffect(() => { if (org) setPaidUntil(org.paidUntil?.slice(0, 10) ?? ''); }, [org]);

  // App-org mapping form
  const [wapsignOrgId, setWapsignOrgId] = useState('');
  const [wapmeetOrgId, setWapmeetOrgId] = useState('');
  const [wapstatOrgId, setWapstatOrgId] = useState('');
  const [wapproOrgId, setWapproOrgId] = useState('');
  const [wapdocsOrgId, setWapdocsOrgId] = useState('');
  const [mapMsg, setMapMsg] = useState<string | null>(null);

  useEffect(() => {
    if (org) {
      setWapsignOrgId(org.wapsignOrgId ?? '');
      setWapmeetOrgId(org.wapmeetOrgId ?? '');
      setWapstatOrgId(org.wapstatOrgId ?? '');
      setWapproOrgId(org.wapproOrgId ?? '');
      setWapdocsOrgId(org.wapdocsOrgId ?? '');
    }
  }, [org]);

  const invalidateUsers = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'users', id] });
  };
  const invalidateDetail = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'detail', id] });
  };
  const invalidateAppRoles = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'app-roles', id] });
  };

  const setSub = useMutation({
    mutationFn: () => subscriptionsApi.setDate(id, paidUntil || null),
    onSuccess: () => invalidateDetail(),
  });

  const saveName = useMutation({
    mutationFn: (patch: { name: string; nameDv: string }) => orgsApi.updateOrg(id, patch),
    onSuccess: () => invalidateDetail(),
  });

  const invite = useMutation({
    mutationFn: () =>
      orgsApi.bootstrapOrgAdmin(id, email, { appProvisioning: toAppProvisioning(appAccess) }),
    onSuccess: (r) => {
      setResult(r);
      setInviteError(null);
      setEmail('');
      setAppAccess(emptyAppAccess);
    },
    onError: () => setInviteError('Failed to create invite. Check the email address.'),
  });

  const update = useMutation({
    mutationFn: ({ userId, patch }: { userId: string; patch: OrgUserPatch }) =>
      orgsApi.updateUser(id, userId, patch),
    onSuccess: invalidateUsers,
  });

  const resetPassword = useMutation({
    mutationFn: (userId: string) => orgsApi.resetUserPassword(id, userId),
    onSuccess: (_d, userId) => setResetDone((s) => ({ ...s, [userId]: true })),
  });

  const [resyncSummary, setResyncSummary] = useState<ResyncSummary | null>(null);
  const resync = useMutation({
    mutationFn: () => orgsApi.resync(id),
    onSuccess: (s) => {
      setResyncSummary(s);
      invalidateAppRoles();
    },
    onError: () => setResyncSummary(null),
  });

  const enable = useMutation({
    mutationFn: (app: AppKey) => orgsApi.enableApp(id, app),
    onSuccess: () => invalidateDetail(),
  });

  const disable = useMutation({
    mutationFn: (app: AppKey) => orgsApi.disableApp(id, app),
    onSuccess: () => invalidateDetail(),
  });

  const saveMapping = useMutation({
    mutationFn: () =>
      orgsApi.patch(id, {
        wapsignOrgId: wapsignOrgId || null,
        wapmeetOrgId: wapmeetOrgId || null,
        wapstatOrgId: wapstatOrgId || null,
        wapproOrgId: wapproOrgId || null,
        wapdocsOrgId: wapdocsOrgId || null,
      }),
    onSuccess: () => {
      setMapMsg('Saved.');
      invalidateDetail();
    },
    onError: () => setMapMsg('Could not save mapping.'),
  });

  return (
    <div className="page space-y-6">
      <Link to="/admin/orgs" className="text-sm text-primary hover:underline">
        ← All orgs
      </Link>
      {org ? (
        <>
          <OrgNameHeader
            org={org}
            onSave={(patch) => saveName.mutate(patch)}
            busy={saveName.isPending}
            error={saveName.isError}
            savedAt={saveName.isSuccess ? saveName.submittedAt : 0}
          />

          {/* Subscription panel */}
          <section className="card-paper p-4 sm:p-5 space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">Subscription</h2>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <label htmlFor="paidUntil" className="field-label">Paid until</label>
                <input id="paidUntil" type="date" value={paidUntil} onChange={(e) => setPaidUntil(e.target.value)} className="input w-auto" />
              </div>
              <button type="button" className="btn-primary" disabled={setSub.isPending} onClick={() => setSub.mutate()}>
                {setSub.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </section>

          {/* Apps panel */}
          <section className="card-paper p-4 sm:p-5 space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              Apps
            </h2>
            <div className="divide-y divide-border/50">
              {APP_ROWS.map(({ key, name }) => {
                const enabledId =
                  org[`${key}OrgId` as 'wapmeetOrgId' | 'wapstatOrgId' | 'wapsignOrgId' | 'wapproOrgId' | 'wapdocsOrgId'];
                const busy =
                  (enable.isPending && enable.variables === key) ||
                  (disable.isPending && disable.variables === key);
                return (
                  <div key={key} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium">{name}</span>
                      {enabledId && (
                        <span className="block truncate text-xs text-muted-foreground">
                          Enabled · {enabledId}
                        </span>
                      )}
                    </div>
                    {enabledId ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Disable ${name} for this council? This unlinks it from Office; the ${name} data is kept and can be re-enabled later.`))
                            disable.mutate(key);
                        }}
                        disabled={busy}
                        className="btn-secondary btn-sm shrink-0"
                      >
                        {disable.isPending && disable.variables === key ? 'Disabling…' : 'Disable'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => enable.mutate(key)}
                        disabled={busy}
                        className="btn-primary btn-sm shrink-0"
                      >
                        {enable.isPending && enable.variables === key ? 'Enabling…' : 'Enable'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {enable.isError && (
              <p className="text-sm text-destructive">
                Couldn&rsquo;t enable that app — it may be unreachable. Try again.
              </p>
            )}
            {disable.isError && (
              <p className="text-sm text-destructive">
                Couldn&rsquo;t disable that app — try again.
              </p>
            )}
          </section>

          {/* App-org mapping — collapsed escape hatch. The Apps panel above
              handles the normal enable/disable; this is only for linking a
              council that ALREADY has an org in an app (so one-click Enable
              won't create a duplicate). */}
          <details className="card-paper p-4 sm:p-5 [&_form]:mt-3 [&[open]_summary]:mb-1">
            <summary className="flex cursor-pointer select-none items-center gap-2 text-sm font-medium text-muted-foreground">
              <Icon name="link" size={18} /> Advanced — link an existing app org by id
            </summary>
            <p className="text-sm text-muted-foreground mt-2">
              Only needed if this council already has an org in an app (created
              before Office) — paste its id here to link the existing one instead
              of creating a new one with Enable. Clear a field to unlink.
            </p>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setMapMsg(null);
                saveMapping.mutate();
              }}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="wapsignOrgId" className="field-label">
                    WapSign org id
                  </label>
                  <input
                    id="wapsignOrgId"
                    value={wapsignOrgId}
                    onChange={(e) => setWapsignOrgId(e.target.value)}
                    placeholder="(none)"
                    className="input font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="wapmeetOrgId" className="field-label">
                    WapMeet org id
                  </label>
                  <input
                    id="wapmeetOrgId"
                    value={wapmeetOrgId}
                    onChange={(e) => setWapmeetOrgId(e.target.value)}
                    placeholder="(none)"
                    className="input font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="wapstatOrgId" className="field-label">
                    WapStat org id
                  </label>
                  <input
                    id="wapstatOrgId"
                    value={wapstatOrgId}
                    onChange={(e) => setWapstatOrgId(e.target.value)}
                    placeholder="(none)"
                    className="input font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="wapproOrgId" className="field-label">
                    WapPro org id
                  </label>
                  <input
                    id="wapproOrgId"
                    value={wapproOrgId}
                    onChange={(e) => setWapproOrgId(e.target.value)}
                    placeholder="(none)"
                    className="input font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="wapdocsOrgId" className="field-label">
                    WapDocs org id
                  </label>
                  <input
                    id="wapdocsOrgId"
                    value={wapdocsOrgId}
                    onChange={(e) => setWapdocsOrgId(e.target.value)}
                    placeholder="(none)"
                    className="input font-mono"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={saveMapping.isPending} className="btn-primary">
                  <Icon name="save" size={18} /> {saveMapping.isPending ? 'Saving…' : 'Save mapping'}
                </button>
                {mapMsg && <span className="text-sm text-muted-foreground">{mapMsg}</span>}
              </div>
            </form>
          </details>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-medium text-muted-foreground">Users</h2>
              <button
                type="button"
                disabled={resync.isPending}
                onClick={() => {
                  setResyncSummary(null);
                  resync.mutate();
                }}
                className="btn-secondary btn-sm"
                title="Re-push every user's active status to WapSign and WapMeet"
              >
                <Icon name="refresh" size={16} className={resync.isPending ? "animate-spin" : ""} />
                {resync.isPending ? 'Syncing…' : 'Re-sync to apps'}
              </button>
            </div>
            {resync.isError && (
              <p className="text-sm text-destructive">Re-sync failed — please try again.</p>
            )}
            {resyncSummary && (
              <p className="text-sm text-muted-foreground">
                Synced {resyncSummary.users} user(s). WapSign: {resyncSummary.wapsign.ok} ok
                {resyncSummary.wapsign.failed > 0 ? `, ${resyncSummary.wapsign.failed} failed` : ''}.
                WapMeet: {resyncSummary.wapmeet.ok} ok
                {resyncSummary.wapmeet.failed > 0 ? `, ${resyncSummary.wapmeet.failed} failed` : ''}.
                WapStat: {resyncSummary.wapstat.ok} ok
                {resyncSummary.wapstat.failed > 0 ? `, ${resyncSummary.wapstat.failed} failed` : ''}.
                WapPro: {resyncSummary.wappro.ok} ok
                {resyncSummary.wappro.failed > 0 ? `, ${resyncSummary.wappro.failed} failed` : ''}.
                WapDocs: {resyncSummary.wapdocs.ok} ok
                {resyncSummary.wapdocs.failed > 0 ? `, ${resyncSummary.wapdocs.failed} failed` : ''}.
              </p>
            )}
            {(update.isError || resetPassword.isError) && (
              <p className="text-sm text-destructive">Something went wrong — the change was not applied.</p>
            )}
            <div className="card-paper divide-y divide-border/50 overflow-hidden">
              {users?.map((u) => {
                const isSysAdmin = u.role === 'SYS_ADMIN';
                const busy = update.isPending && update.variables?.userId === u.id;
                return (
                  <div key={u.id} className="p-3 space-y-2.5">
                   <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground">{u.name}</span>
                        {u.nameDv && (
                          <span dir="rtl" className="font-dhivehi text-base text-foreground">
                            {u.nameDv}
                          </span>
                        )}
                        <RoleBadge user={u} />
                        <span className={`pill ${u.isActive ? 'pill-success' : 'pill-neutral'}`}>
                          {u.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                      <AppRoleChips email={u.email} roles={appRoles} />
                    </div>
                    {isSysAdmin ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditAppRoles((s) => ({ ...s, [u.id]: !s[u.id] }))}
                          className="btn-secondary btn-sm"
                          aria-expanded={!!editAppRoles[u.id]}
                        >
                          <Icon name="tune" size={16} /> Edit app roles
                        </button>
                        {u.isOrgAdmin ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              if (confirm(`Remove admin rights from ${u.name}?`))
                                update.mutate({ userId: u.id, patch: { isOrgAdmin: false } });
                            }}
                            className="btn-secondary btn-sm"
                          >
                            Remove admin
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              if (confirm(`Make ${u.name} an admin?`))
                                update.mutate({ userId: u.id, patch: { isOrgAdmin: true } });
                            }}
                            className="btn-secondary btn-sm"
                          >
                            Make admin
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => update.mutate({ userId: u.id, patch: { isActive: !u.isActive } })}
                          className="btn-secondary btn-sm"
                        >
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          type="button"
                          disabled={resetPassword.isPending}
                          onClick={() => {
                            if (confirm(`Send a password reset email to ${u.email}?`))
                              resetPassword.mutate(u.id);
                          }}
                          className="btn-secondary btn-sm"
                        >
                          Reset password
                        </button>
                        {resetDone[u.id] && (
                          <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]">
                            <Icon name="mark_email_read" size={16} /> Sent
                          </span>
                        )}
                      </div>
                    )}
                   </div>
                    {!isSysAdmin && (
                      <UserEditor
                        user={u}
                        busy={busy}
                        onSave={(patch) => update.mutate({ userId: u.id, patch })}
                      />
                    )}
                    {!isSysAdmin && editAppRoles[u.id] && (
                      <AppRoleEditor
                        user={u}
                        roles={appRoles}
                        hasWapsign={!!org.wapsignOrgId}
                        hasWapmeet={!!org.wapmeetOrgId}
                        hasWapstat={!!org.wapstatOrgId}
                        hasWappro={!!org.wapproOrgId}
                        hasWapdocs={!!org.wapdocsOrgId}
                        updateAppRoles={(userId, body) => orgsApi.updateAppRoles(id, userId, body)}
                        provisionApp={(userId, body) => orgsApi.provisionApp(id, userId, body)}
                        onDone={invalidateAppRoles}
                      />
                    )}
                  </div>
                );
              })}
              {users && users.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">No users yet.</p>
              )}
            </div>
          </section>

          <section className="card-paper p-4 sm:p-5 space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">Invite the first org admin</h2>
            <p className="text-sm text-muted-foreground">
              Send the resulting link to the person who&rsquo;ll own this organization. They&rsquo;ll pick
              a password and become an org admin.
            </p>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) invite.mutate();
              }}
            >
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  aria-label="Org admin email"
                  type="email"
                  placeholder="org-admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input flex-1"
                />
                <button
                  type="submit"
                  disabled={invite.isPending || !email.trim() || !!appAccessError(appAccess)}
                  className="btn-primary"
                >
                  {invite.isPending ? 'Creating…' : 'Create invite'}
                </button>
              </div>
              <AppAccessFields
                value={appAccess}
                onChange={setAppAccess}
                hasWapsign={!!org.wapsignOrgId}
                hasWapmeet={!!org.wapmeetOrgId}
                hasWapstat={!!org.wapstatOrgId}
                hasWappro={!!org.wapproOrgId}
                hasWapdocs={!!org.wapdocsOrgId}
              />
            </form>
            {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}
            {result && (
              <div className="flex items-start gap-3 rounded-xl bg-[hsl(var(--secondary-container))] p-4 text-[hsl(var(--on-secondary-container))]">
                <Icon name="mark_email_read" size={20} className="mt-0.5 shrink-0" />
                <p className="text-xs">
                  Invite emailed to <span className="font-medium">{result.invite.email}</span>
                  . Link expires {new Date(result.invite.expiresAt).toLocaleString()}.
                </p>
              </div>
            )}
          </section>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          Loading…
        </p>
      )}
    </div>
  );
}
